// frontend/src/components/GoLivePanel.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useChecklist } from "../contexts/ChecklistContext";

// Inject shimmer animation once
if (
  typeof document !== "undefined" &&
  !document.getElementById("golive-shimmer-style")
) {
  const style = document.createElement("style");
  style.id = "golive-shimmer-style";
  style.textContent = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
}

/**
 * SetupChecklistSection - Shows remaining setup requirements with clickable links
 * Navigation mapping for each requirement type
 */
const getRequirementNavigation = (reqName, type, id) => {
  const isFreelancer = type === "freelancer";
  const basePath = isFreelancer ? "/dashboard/freelancer" : "/dashboard";

  // Lowercase name for easier matching
  const name = reqName.toLowerCase();

  // Freelancer-specific requirements
  if (isFreelancer) {
    if (name.includes("brand name") || name.includes("brand logo")) {
      return `${basePath}/settings`;
    }
    if (name.includes("stripe")) {
      return `${basePath}/settings`;
    }
    if (name.includes("service")) {
      return `${basePath}/services`;
    }
    if (
      name.includes("profile") ||
      name.includes("name") ||
      name.includes("photo")
    ) {
      return `${basePath}/profile`;
    }
    if (name.includes("schedule") || name.includes("availability")) {
      return `${basePath}/schedule`;
    }
    if (name.includes("booking")) {
      return `${basePath}/settings`;
    }
    if (name.includes("migration")) {
      return `${basePath}/migration/${id}`;
    }
    return `${basePath}/settings`;
  }

  // Business-specific requirements
  if (name.includes("stripe")) {
    return `${basePath}/settings`;
  }
  if (
    name.includes("business profile") ||
    name.includes("name") ||
    name.includes("logo")
  ) {
    return `${basePath}/settings`;
  }
  if (
    name.includes("location") ||
    name.includes("shop") ||
    name.includes("hours") ||
    name.includes("pictures")
  ) {
    return `${basePath}/locations`;
  }
  if (name.includes("professional") && name.includes("added")) {
    return `${basePath}/staff`;
  }
  if (name.includes("professional") && name.includes("service")) {
    return `${basePath}/services`;
  }
  if (name.includes("professional") && name.includes("schedule")) {
    return `${basePath}/schedules`;
  }
  if (name.includes("migration")) {
    return `${basePath}/migration/${id}`;
  }

  return `${basePath}/settings`;
};

const SetupChecklistSection = ({
  requirements,
  completionPercentage,
  type,
  id,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Count incomplete required items
  const incompleteRequired = requirements.filter(
    (r) => r.required && !r.completed,
  );
  const allComplete = incompleteRequired.length === 0;

  if (allComplete) {
    return (
      <div style={checklistStyles.container}>
        <div style={checklistStyles.successBanner}>
          <span style={checklistStyles.successIcon}>✅</span>
          <span style={checklistStyles.successText}>
            All setup requirements complete!
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={checklistStyles.container}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={checklistStyles.toggleButton}
      >
        <span style={checklistStyles.toggleIcon}>{isExpanded ? "▼" : "▶"}</span>
        <span style={checklistStyles.toggleText}>
          Setup Checklist ({incompleteRequired.length} remaining)
        </span>
      </button>

      {isExpanded && (
        <ul style={checklistStyles.list}>
          {requirements.map((req, index) => (
            <li
              key={index}
              style={{
                ...checklistStyles.item,
                ...(req.completed ? checklistStyles.itemComplete : {}),
              }}
            >
              <span
                style={
                  req.completed
                    ? checklistStyles.checkmark
                    : checklistStyles.cross
                }
              >
                {req.completed ? "✅" : "❌"}
              </span>
              {req.completed ? (
                <span style={checklistStyles.reqName}>{req.name}</span>
              ) : (
                <button
                  onClick={() => {
                    const path = getRequirementNavigation(req.name, type, id);
                    window.location.href = path;
                  }}
                  style={checklistStyles.reqLink}
                >
                  {req.name}
                  <span style={checklistStyles.linkArrow}>→</span>
                </button>
              )}
              {req.required && !req.completed && (
                <span style={checklistStyles.requiredBadge}>Required</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const checklistStyles = {
  container: {
    margin: "16px 0",
    borderRadius: "8px",
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
  },
  successBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 16px",
    gap: "8px",
  },
  successIcon: {
    fontSize: "18px",
  },
  successText: {
    color: "#059669",
    fontWeight: "500",
    fontSize: "14px",
  },
  toggleButton: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "12px 16px",
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    borderRadius: "8px",
  },
  toggleIcon: {
    fontSize: "10px",
    color: "#6b7280",
    marginRight: "8px",
  },
  toggleText: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
  },
  list: {
    listStyle: "none",
    margin: "0",
    padding: "0 16px 16px",
  },
  item: {
    display: "flex",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #e5e7eb",
    gap: "8px",
  },
  itemComplete: {
    opacity: 0.6,
  },
  checkmark: {
    fontSize: "14px",
    flexShrink: 0,
  },
  cross: {
    fontSize: "14px",
    flexShrink: 0,
  },
  reqName: {
    flex: 1,
    fontSize: "13px",
    color: "#6b7280",
  },
  reqLink: {
    flex: 1,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontSize: "13px",
    color: "#2563eb",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  linkArrow: {
    fontSize: "12px",
    opacity: 0.7,
  },
  requiredBadge: {
    fontSize: "10px",
    fontWeight: "600",
    color: "#dc2626",
    backgroundColor: "#fef2f2",
    padding: "2px 6px",
    borderRadius: "4px",
    textTransform: "uppercase",
  },
};

/**
 * GoLivePanel - Reusable component for business and freelancer go-live functionality
 *
 * Props:
 * - type: 'business' or 'freelancer'
 * - id: businessId or staffId
 * - onGoLive: Optional callback when go-live is activated
 */
const GoLivePanel = ({ type = "business", id, onGoLive }) => {
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  const [showSwitchConfirmModal, setShowSwitchConfirmModal] = useState(false);
  const [switchDirection, setSwitchDirection] = useState(null); // 'import' or 'fresh'
  const [goLiveIntent, setGoLiveIntent] = useState(null);

  // Use checklist context for optimistic updates
  const { checklist, setChecklistData } = useChecklist();

  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  useEffect(() => {
    fetchChecklist();
  }, [id, type]);

  // Reduced polling: Only check every 60 seconds for background validation
  useEffect(() => {
    if (!id || !checklist) return;

    const isComplete = checklist.completionPercentage === 100;

    if (!isComplete && !loading) {
      const pollInterval = setInterval(() => {
        fetchChecklist();
      }, 60000); // Poll every 60 seconds (reduced from 15)

      return () => clearInterval(pollInterval);
    }
  }, [id, checklist?.completionPercentage, loading]);

  const fetchChecklist = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        throw new Error("Not authenticated");
      }

      const endpoint =
        type === "business"
          ? `/api/migration/business/${id}/go-live-checklist`
          : `/api/migration/freelancer/${id}/go-live-checklist`;
      const response = await axios.get(`${apiBaseUrl}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChecklistData(response.data);

      // Extract intent from known payload shapes and safely default to pending.
      const payloadIntent =
        response.data?.staff?.goLiveIntent ||
        response.data?.business?.goLiveIntent ||
        response.data?.goLiveIntent;
      const inferredIntent =
        payloadIntent ||
        (response.data?.migration?.sourcePlatform &&
        response.data.migration.sourcePlatform !== "none"
          ? "import"
          : "pending");

      setGoLiveIntent(inferredIntent);
    } catch (err) {
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
      }
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIntent = async (newIntent) => {
    try {
      setActivating(true);
      setError(null);

      const token = localStorage.getItem("ogestyle_token");
      const endpoint =
        type === "business"
          ? `/api/migration/business/${id}/go-live-intent`
          : `/api/migration/freelancer/${id}/go-live-intent`;

      await axios.patch(
        `${apiBaseUrl}${endpoint}`,
        { intent: newIntent },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setGoLiveIntent(newIntent);

      // Prevent stale scheduled-date UI from flashing when switching to fresh.
      if (newIntent === "fresh" && checklist) {
        setChecklistData({
          ...checklist,
          migration: {
            ...checklist.migration,
            goLiveDate: null,
            migrationGoLiveDate: null,
          },
        });
      }

      // If switching to import, redirect to migration wizard
      if (newIntent === "import") {
        const migrationPath =
          type === "freelancer"
            ? `/dashboard/freelancer/migration/${id}`
            : `/dashboard/migration/${id}`;
        window.location.href = migrationPath;
      } else {
        // Refresh checklist
        await fetchChecklist();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActivating(false);
    }
  };

  const handleResetMigration = async () => {
    try {
      setActivating(true);
      setError(null);

      const token = localStorage.getItem("ogestyle_token");
      const endpoint =
        type === "business"
          ? `/api/migration/business/${id}/reset-migration`
          : `/api/migration/freelancer/${id}/reset-migration`;

      await axios.patch(
        `${apiBaseUrl}${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setGoLiveIntent("fresh");
      setShowSwitchConfirmModal(false);
      await fetchChecklist();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActivating(false);
    }
  };

  const handleSwitchPath = (direction) => {
    setSwitchDirection(direction);
    setShowSwitchConfirmModal(true);
  };

  const confirmSwitchPath = async () => {
    if (switchDirection === "fresh") {
      // Switching from import to fresh - reset migration
      await handleResetMigration();
    } else if (switchDirection === "import") {
      // Switching from fresh to import
      await handleUpdateIntent("import");
    }
    setShowSwitchConfirmModal(false);
  };

  const handleGoLiveNow = async () => {
    if (
      !window.confirm(
        "Are you ready to go live? Your trial period will start immediately.",
      )
    ) {
      return;
    }

    try {
      setActivating(true);
      setError(null);

      const token = localStorage.getItem("ogestyle_token");
      const endpoint =
        type === "business"
          ? `/api/migration/business/${id}/go-live`
          : `/api/migration/freelancer/${id}/go-live`;

      const response = await axios.post(
        `${apiBaseUrl}${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Refresh checklist
      await fetchChecklist();

      if (onGoLive) {
        onGoLive(response.data);
      }

      alert(response.data.message || "You are now live! 🎉");
    } catch (err) {
      setError(err.response?.data?.error || err.message);

      if (err.response?.data?.incompleteRequirements) {
        alert(
          `Cannot go live yet. Missing:\n- ${err.response.data.incompleteRequirements.join("\n- ")}`,
        );
      }
    } finally {
      setActivating(false);
    }
  };

  const handleScheduleGoLive = async () => {
    if (
      !window.confirm(
        "Schedule go-live using the platform default window from your signup date?",
      )
    ) {
      return;
    }

    try {
      setActivating(true);
      setError(null);

      const token = localStorage.getItem("ogestyle_token");
      const endpoint =
        type === "business"
          ? `/api/migration/business/${id}/go-live`
          : `/api/migration/freelancer/${id}/go-live`;

      const response = await axios.post(
        `${apiBaseUrl}${endpoint}`,
        { scheduleGoLive: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await fetchChecklist();

      alert(response.data.message || "Go-live scheduled successfully!");
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.panel}>
        <div style={styles.loadingSkeleton}>
          <div style={styles.skeletonHeader}></div>
          <div style={styles.skeletonProgressBar}></div>
          <div style={styles.skeletonText}></div>
          <div style={styles.skeletonText}></div>
        </div>
      </div>
    );
  }

  if (error && !checklist) {
    return (
      <div style={styles.panel}>
        <div style={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  const isLive = checklist?.migration?.isLive;
  const goLiveDate = checklist?.migration?.goLiveDate;
  const trialEndDate = checklist?.migration?.trialEndDate;
  const resolvedGoLiveIntent =
    goLiveIntent ||
    checklist?.staff?.goLiveIntent ||
    checklist?.business?.goLiveIntent ||
    (checklist?.migration?.sourcePlatform &&
    checklist.migration.sourcePlatform !== "none"
      ? "import"
      : "pending");
  // STAGE 1: Show initial choice when intent is "pending" (not gated by 100%)
  // The user needs to pick import vs fresh BEFORE they can complete checklist items
  if (resolvedGoLiveIntent === "pending" && !isLive) {
    return (
      <div style={styles.panel}>
        <div style={styles.stage1Header}>
          <h3 style={styles.stage1Title}>
            🎯 How Would You Like to Get Started?
          </h3>
          <p style={styles.stage1Subtitle}>Choose your path to go live:</p>
        </div>

        <div style={styles.stage1Options}>
          <button
            onClick={() => handleUpdateIntent("import")}
            disabled={activating}
            style={styles.stage1Button}
            onMouseOver={(e) =>
              (e.currentTarget.style.transform = "scale(1.02)")
            }
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <span style={styles.stage1Icon}>🔄</span>
            <h4 style={styles.stage1ButtonTitle}>
              I'm Migrating from Another Platform
            </h4>
            <p style={styles.stage1ButtonText}>
              Bring your customers, services & bookings
              <br />
              (Square, Booksy, etc.) - <strong>Takes 3 minutes!</strong>
            </p>
          </button>

          <button
            onClick={() => handleUpdateIntent("fresh")}
            disabled={activating}
            style={styles.stage1Button}
            onMouseOver={(e) =>
              (e.currentTarget.style.transform = "scale(1.02)")
            }
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <span style={styles.stage1Icon}>✨</span>
            <h4 style={styles.stage1ButtonTitle}>
              I'm Starting Fresh - New Business
            </h4>
            <p style={styles.stage1ButtonText}>
              Perfect for brand new businesses
              <br />
              Add your services manually
            </p>
          </button>
        </div>

        {/* Show setup checklist - helps users see what's needed before choosing path */}
        {checklist?.requirements && checklist.requirements.length > 0 && (
          <SetupChecklistSection
            requirements={checklist.requirements}
            completionPercentage={checklist.completionPercentage}
            type={type}
            id={id}
          />
        )}

        {/* Show current progress info */}
        {checklist?.completionPercentage !== undefined && (
          <p
            style={{
              margin: "16px 0 0",
              fontSize: "13px",
              color: "#6b7280",
              textAlign: "center",
            }}
          >
            Setup progress: {checklist.completionPercentage}% complete
          </p>
        )}

        {error && <div style={styles.errorMessage}>{error}</div>}
      </div>
    );
  }

  if (error && !checklist) {
    return (
      <div style={styles.panel}>
        <div style={styles.error}>Error: {error}</div>
        <button onClick={fetchChecklist} style={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  // If already live
  if (isLive && goLiveDate) {
    // Check if requirements are 100% complete
    const isFullyComplete = checklist?.completionPercentage === 100;
    const liveRequirements = checklist?.requirements || [];

    // Only hide if 100% complete
    if (isFullyComplete) {
      return null;
    }

    return (
      <div style={styles.panel}>
        <div style={styles.liveHeader}>
          <span style={styles.liveIcon}>🟢</span>
          <h3 style={styles.liveTitle}>You're Live!</h3>
        </div>
        <p style={styles.liveMessage}>
          Your {type} went live on{" "}
          {new Date(goLiveDate).toLocaleDateString("en-US", {
            timeZone: "UTC",
          })}
        </p>
        <p
          style={{
            margin: "0 0 12px 0",
            fontSize: "13px",
            color: "#4b5563",
          }}
        >
          You are live. Complete the remaining setup items below to improve
          visibility and finish your onboarding.
        </p>
        {trialEndDate && (
          <p style={styles.trialInfo}>
            Trial ends: {new Date(trialEndDate).toLocaleDateString()}
          </p>
        )}

        {/* Keep checklist visible after go-live so owners can finish remaining setup */}
        {liveRequirements.length > 0 && (
          <SetupChecklistSection
            requirements={liveRequirements}
            completionPercentage={checklist?.completionPercentage || 0}
            type={type}
            id={id}
          />
        )}

        {/* Option to continue importing customers after going live */}
        <div style={styles.liveImportOptions}>
          <p style={styles.liveImportText}>
            You can continue importing customers to keep your data up to date.
          </p>
          <button
            onClick={() => {
              const importPath =
                type === "freelancer"
                  ? `/dashboard/freelancer/customers`
                  : `/dashboard/customers`;
              window.location.href = importPath;
            }}
            style={styles.liveImportButton}
          >
            👥 Manage Customers
          </button>
        </div>
      </div>
    );
  }

  // If scheduled but not live yet - show checklist with scheduled message
  if (goLiveDate && !isLive && !activating) {
    const isMigrationUser =
      resolvedGoLiveIntent === "import" ||
      (checklist?.migration?.sourcePlatform &&
        checklist?.migration?.sourcePlatform !== "none");

    const { requirements, completionPercentage } = checklist;

    return (
      <div style={styles.panel}>
        <div style={styles.scheduledHeader}>
          <span style={styles.scheduledIcon}>📅</span>
          <h3 style={styles.scheduledTitle}>Go-Live Scheduled</h3>
        </div>
        <p style={styles.scheduledMessage}>
          Your {type === "freelancer" ? "profile" : "business"} is scheduled to
          go live on{" "}
          <strong>
            {new Date(goLiveDate).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })}
          </strong>
        </p>

        {isMigrationUser && (
          <div style={styles.scheduledImportSection}>
            <p style={styles.scheduledImportText}>
              📦 You can continue importing customers and appointments until
              your go-live date to keep your data in sync.
            </p>
            <div style={styles.scheduledImportButtons}>
              <button
                onClick={() => {
                  const importPath =
                    type === "freelancer"
                      ? `/dashboard/freelancer/customers`
                      : `/dashboard/customers`;
                  window.location.href = importPath;
                }}
                style={styles.scheduledImportButton}
              >
                👥 Import Customers
              </button>
              {/* DISABLED: Import Appointments - Must be done AFTER staff are imported
                  Reason: Appointments need to match staff names. Import staff first,
                  then import appointments last to avoid assignment errors.
              <button
                onClick={() => {
                  const importPath =
                    type === "freelancer"
                      ? `/dashboard/freelancer/bookings`
                      : `/dashboard/bookings`;
                  window.location.href = importPath;
                }}
                style={styles.scheduledImportButton}
              >
                📅 Import Appointments
              </button>
              */}
            </div>
          </div>
        )}

        {/* Show setup checklist */}
        {requirements && requirements.length > 0 && (
          <SetupChecklistSection
            requirements={requirements}
            completionPercentage={completionPercentage}
            type={type}
            id={id}
          />
        )}
      </div>
    );
  }

  // STAGE 2: If user has chosen import or fresh intent, show appropriate actions
  // This replaces the checklist after they make their choice
  if (
    (resolvedGoLiveIntent === "import" ||
      resolvedGoLiveIntent === "fresh") &&
    !isLive
  ) {
    // Check if this is a migration scenario (user chose import AND has started importing)
    const isMigrating =
      resolvedGoLiveIntent === "import" &&
      checklist?.migration?.sourcePlatform &&
      checklist?.migration?.sourcePlatform !== "none";

    // Check if user chose import but hasn't started yet
    const isImportIntentOnly =
      resolvedGoLiveIntent === "import" &&
      (!checklist?.migration?.sourcePlatform ||
        checklist?.migration?.sourcePlatform === "none");

    const { ready, requirements, completionPercentage } = checklist;

    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {isMigrating
              ? `🚀 Migrating from ${checklist.migration.sourcePlatform.charAt(0).toUpperCase() + checklist.migration.sourcePlatform.slice(1)}`
              : isImportIntentOnly
                ? "🚀 Ready to Import Data"
                : "🚀 Go Live Checklist"}
          </h3>
          <span style={styles.percentage}>{completionPercentage}% Ready</span>
        </div>

        <div style={styles.progressBar}>
          <div
            style={{
              ...styles.progressFill,
              width: `${completionPercentage}%`,
            }}
          />
        </div>

        {isImportIntentOnly && (
          <div style={styles.migrationInfo}>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#374151",
              }}
            >
              📦 <strong>Start importing your data</strong> from your previous
              platform. The wizard will guide you through uploading and mapping
              your data in just 3-5 minutes.
            </p>
          </div>
        )}
        {isMigrating && (
          <div style={styles.migrationInfo}>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#374151",
              }}
            >
              📦 <strong>Continue importing data</strong> from your previous
              platform until your scheduled go-live date. You can import new
              appointments and customers multiple times before going live.
            </p>
            {checklist.migration.migrationGoLiveDate && (
              <p style={{ margin: "0", fontSize: "13px", color: "#6b7280" }}>
                Scheduled go-live:{" "}
                <strong>
                  {new Date(
                    checklist.migration.migrationGoLiveDate,
                  ).toLocaleDateString()}
                </strong>
              </p>
            )}
          </div>
        )}

        {error && <div style={styles.errorMessage}>{error}</div>}

        {/* Setup Checklist - shows remaining requirements */}
        {requirements && requirements.length > 0 && (
          <SetupChecklistSection
            requirements={requirements}
            completionPercentage={completionPercentage}
            type={type}
            id={id}
          />
        )}

        <div style={styles.actions}>
          {isMigrating ? (
            <>
              {/* If go-live date is scheduled, show individual import options instead of full wizard */}
              {checklist.migration.migrationGoLiveDate ? (
                <>
                  {/* Individual import options - shown after go-live is scheduled */}
                  <div style={styles.importOptionsContainer}>
                    <p style={styles.importOptionsLabel}>
                      Import additional data:
                    </p>
                    <div style={styles.importButtonsRow}>
                      <button
                        onClick={() => {
                          const importPath =
                            type === "freelancer"
                              ? `/dashboard/freelancer/customers/import`
                              : type === "booth-renter"
                                ? `/dashboard/booth-renter/customers/import`
                              : `/dashboard/customers/import`;
                          window.location.href = importPath;
                        }}
                        style={styles.importOptionButton}
                      >
                        👥 Import Customers
                      </button>
                      {/* DISABLED: Import Appointments - Must be done AFTER staff are imported
                          Reason: Appointments need to match staff names. Import staff first,
                          then import appointments last to avoid assignment errors.
                      <button
                        onClick={() => {
                          const importPath =
                            type === "freelancer"
                              ? `/dashboard/freelancer/bookings/import`
                              : `/dashboard/bookings/import`;
                          window.location.href = importPath;
                        }}
                        style={styles.importOptionButton}
                      >
                        📅 Import Appointments
                      </button>
                      */}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Full wizard - shown before go-live is scheduled */}
                  <button
                    onClick={handleGoLiveNow}
                    disabled={!ready || activating}
                    style={
                      ready ? styles.goLiveButton : styles.goLiveButtonDisabled
                    }
                  >
                    {activating ? "Activating..." : "🚀 Go Live Now"}
                  </button>
                  <button
                    onClick={() => {
                      const migrationPath =
                        type === "freelancer"
                          ? `/dashboard/freelancer/migration/${id}`
                          : `/dashboard/migration/${id}`;
                      window.location.href = migrationPath;
                    }}
                    style={styles.goLiveButton}
                  >
                    📦 Continue Import
                  </button>
                  <button
                    onClick={handleScheduleGoLive}
                    disabled={activating}
                    style={styles.scheduleButton}
                  >
                    {activating ? "Scheduling..." : "📅 Schedule Go-Live"}
                  </button>
                </>
              )}
            </>
          ) : resolvedGoLiveIntent === "import" ? (
            // User chose import but hasn't started - show button to start wizard
            <>
              <button
                onClick={() => {
                  const migrationPath =
                    type === "freelancer"
                      ? `/dashboard/freelancer/migration/${id}`
                      : `/dashboard/migration/${id}`;
                  window.location.href = migrationPath;
                }}
                style={styles.goLiveButton}
              >
                📦 Start Import Wizard
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleGoLiveNow}
                disabled={!ready || activating}
                style={
                  ready ? styles.goLiveButton : styles.goLiveButtonDisabled
                }
              >
                {activating ? "Activating..." : "🚀 Go Live Now"}
              </button>
              <button
                onClick={handleScheduleGoLive}
                disabled={activating}
                style={styles.scheduleButton}
              >
                {activating ? "Scheduling..." : "📅 Schedule Go-Live"}
              </button>
            </>
          )}
        </div>

        {!ready && !isMigrating && !isImportIntentOnly && (
          <p style={styles.hint}>
            Complete all required items above to go live.
          </p>
        )}

        {/* Link to switch between paths */}
        {isMigrating || isImportIntentOnly ? (
          <p style={styles.switchLink}>
            Changed your mind?{" "}
            <button
              onClick={() => handleSwitchPath("fresh")}
              style={styles.textButton}
            >
              Start fresh instead
            </button>
          </p>
        ) : (
          resolvedGoLiveIntent === "fresh" && (
            <p style={styles.switchLink}>
              Want to{" "}
              <button
                onClick={() => handleSwitchPath("import")}
                style={styles.textButton}
              >
                import data from another platform
              </button>
              ?
            </p>
          )
        )}

        {/* Switch Path Confirmation Modal */}
        {showSwitchConfirmModal && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              {switchDirection === "fresh" ? (
                <>
                  <h4>⚠️ Confirm Switch to Fresh Start</h4>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#374151",
                      marginBottom: "16px",
                    }}
                  >
                    {checklist?.migration?.migrationImportCount > 0 ? (
                      <>
                        You have partially imported data:
                        <br />• Imports completed:{" "}
                        {checklist.migration.migrationImportCount}
                        <br />• Last import:{" "}
                        {checklist.migration.lastImportDate
                          ? new Date(
                              checklist.migration.lastImportDate,
                            ).toLocaleDateString()
                          : "N/A"}
                        <br />
                        <br />
                        Starting fresh will{" "}
                        <strong>clear this imported data</strong>. You can
                        always re-import later.
                      </>
                    ) : (
                      "You haven't imported any data yet. You can always come back and import later if needed."
                    )}
                  </p>
                  <div style={styles.modalActions}>
                    <button
                      onClick={confirmSwitchPath}
                      disabled={activating}
                      style={styles.confirmButton}
                    >
                      {activating ? "Switching..." : "Yes, Start Fresh"}
                    </button>
                    <button
                      onClick={() => setShowSwitchConfirmModal(false)}
                      style={styles.cancelButton}
                    >
                      No, Keep Importing
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h4>🔄 Switch to Import Mode</h4>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#374151",
                      marginBottom: "16px",
                    }}
                  >
                    You'll be redirected to the migration wizard to import your
                    data from another platform. This takes about 3-5 minutes and
                    you can import multiple times before going live.
                  </p>
                  <div style={styles.modalActions}>
                    <button
                      onClick={confirmSwitchPath}
                      disabled={activating}
                      style={styles.confirmButton}
                    >
                      {activating ? "Redirecting..." : "Continue to Import"}
                    </button>
                    <button
                      onClick={() => setShowSwitchConfirmModal(false)}
                      style={styles.cancelButton}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // If checklist not complete, show checklist only (no choice yet)
  const { ready, requirements, completionPercentage } = checklist;

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h3 style={styles.title}>🚀 Go Live Checklist</h3>
        <span style={styles.percentage}>{completionPercentage}% Ready</span>
      </div>

      <div style={styles.progressBar}>
        <div
          style={{ ...styles.progressFill, width: `${completionPercentage}%` }}
        />
      </div>

      <ul style={styles.requirementsList}>
        {requirements?.map((req, index) => (
          <li key={index} style={styles.requirementItem}>
            <span style={req.completed ? styles.checkmark : styles.cross}>
              {req.completed ? "✅" : "❌"}
            </span>
            <span style={styles.requirementName}>{req.name}</span>
            {req.required && <span style={styles.requiredBadge}>Required</span>}
          </li>
        ))}
      </ul>

      {error && <div style={styles.errorMessage}>{error}</div>}

      {completionPercentage === 100 ? (
        <p style={styles.hint}>
          ✅ All requirements complete! Refresh the page to choose your go-live
          path.
        </p>
      ) : (
        <p style={styles.hint}>Complete all required items above to proceed.</p>
      )}
    </div>
  );
};

const styles = {
  panel: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  loadingSkeleton: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  skeletonHeader: {
    width: "60%",
    height: "24px",
    background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
    borderRadius: "6px",
  },
  skeletonProgressBar: {
    width: "100%",
    height: "8px",
    background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
    borderRadius: "4px",
  },
  skeletonText: {
    width: "80%",
    height: "16px",
    background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
    borderRadius: "4px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
  },
  percentage: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#10b981",
    background: "#d1fae5",
    padding: "4px 12px",
    borderRadius: "12px",
  },
  progressBar: {
    width: "100%",
    height: "8px",
    background: "#e5e7eb",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "20px",
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #10b981 0%, #059669 100%)",
    transition: "width 0.3s ease",
  },
  requirementsList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 20px 0",
  },
  requirementItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    marginBottom: "8px",
    background: "#f9fafb",
    borderRadius: "8px",
  },
  checkmark: {
    fontSize: "16px",
  },
  cross: {
    fontSize: "16px",
  },
  requirementName: {
    flex: 1,
    fontSize: "14px",
    color: "#374151",
  },
  requiredBadge: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#dc2626",
    background: "#fee2e2",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  actions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },
  goLiveButton: {
    flex: 1,
    padding: "14px 24px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "transform 0.2s",
  },
  goLiveButtonDisabled: {
    flex: 1,
    padding: "14px 24px",
    background: "#d1d5db",
    color: "#9ca3af",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "not-allowed",
  },
  scheduleButton: {
    padding: "14px 24px",
    background: "#ffffff",
    color: "#374151",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  importOptionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
  },
  importOptionsLabel: {
    fontSize: "13px",
    color: "#6b7280",
    margin: 0,
    fontWeight: "500",
  },
  importButtonsRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  importOptionButton: {
    padding: "12px 20px",
    background: "#f0f9ff",
    color: "#0369a1",
    border: "2px solid #0ea5e9",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
  },
  hint: {
    fontSize: "13px",
    color: "#6b7280",
    textAlign: "center",
    marginTop: "12px",
  },
  liveHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
    position: "relative",
  },
  dismissButton: {
    position: "absolute",
    right: 0,
    top: "-4px",
    background: "transparent",
    border: "none",
    fontSize: "18px",
    color: "#6b7280",
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
    borderRadius: "4px",
    transition: "all 0.2s",
  },
  liveIcon: {
    fontSize: "24px",
  },
  liveTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#10b981",
  },
  liveMessage: {
    fontSize: "14px",
    color: "#374151",
    margin: "0 0 8px 0",
  },
  trialInfo: {
    fontSize: "13px",
    color: "#6b7280",
    margin: 0,
  },
  scheduledHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  scheduledIcon: {
    fontSize: "24px",
  },
  scheduledTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#3b82f6",
  },
  scheduledMessage: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    margin: "0 0 8px 0",
  },
  // New styles for scheduled go-live with import options
  scheduledImportSection: {
    marginTop: "16px",
    padding: "16px",
    backgroundColor: "#f0f9ff",
    borderRadius: "8px",
    border: "1px solid #0ea5e9",
  },
  scheduledImportText: {
    fontSize: "14px",
    color: "#0369a1",
    margin: "0 0 12px 0",
    lineHeight: "1.5",
  },
  scheduledImportButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  scheduledImportButton: {
    padding: "10px 16px",
    backgroundColor: "#0ea5e9",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "background-color 0.2s",
  },
  // New styles for live panel with import options
  liveImportOptions: {
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
  },
  liveImportText: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "0 0 12px 0",
  },
  liveImportButton: {
    padding: "10px 16px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  migrationInfo: {
    backgroundColor: "#eff6ff",
    border: "1px solid #3b82f6",
    borderRadius: "8px",
    padding: "12px 16px",
    margin: "16px 0",
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    background: "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    minWidth: "400px",
    maxWidth: "90%",
  },
  dateInput: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    marginTop: "16px",
    marginBottom: "16px",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
  },
  confirmButton: {
    flex: 1,
    padding: "12px 24px",
    background: "#3b82f6",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  cancelButton: {
    flex: 1,
    padding: "12px 24px",
    background: "#ffffff",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  loading: {
    textAlign: "center",
    color: "#6b7280",
    padding: "40px 0",
  },
  error: {
    color: "#dc2626",
    fontSize: "14px",
    marginBottom: "12px",
  },
  errorMessage: {
    background: "#fee2e2",
    color: "#dc2626",
    padding: "12px",
    borderRadius: "8px",
    fontSize: "13px",
    marginTop: "12px",
  },
  retryButton: {
    padding: "10px 20px",
    background: "#3b82f6",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  // Stage 1 styles
  stage1Header: {
    textAlign: "center",
    marginBottom: "32px",
  },
  stage1Title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#111827",
    margin: "0 0 12px 0",
  },
  stage1Subtitle: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
  },
  stage1Options: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "24px",
  },
  stage1Button: {
    flex: 1,
    padding: "24px",
    background: "#ffffff",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.2s ease",
  },
  stage1Icon: {
    fontSize: "32px",
    display: "block",
    marginBottom: "12px",
  },
  stage1ButtonTitle: {
    fontSize: "17px",
    fontWeight: "600",
    color: "#111827",
    margin: "0 0 8px 0",
  },
  stage1ButtonText: {
    fontSize: "14px",
    color: "#6b7280",
    margin: 0,
    lineHeight: "1.5",
  },
  switchLink: {
    textAlign: "center",
    fontSize: "14px",
    color: "#6b7280",
    marginTop: "16px",
  },
  textButton: {
    background: "none",
    border: "none",
    color: "#3b82f6",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: "14px",
    padding: 0,
  },
};

export default GoLivePanel;
